import { useEffect, useState } from "react";
import { api, pollJob, rateLimitMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/Loader";
import EmailQueueChip from "@/components/EmailQueueChip";
import AutoFulfillEtaChip from "@/components/AutoFulfillEtaChip";
import {
    Send, Inbox, Sparkles, Target, Users, Mail, TrendingUp, Plus, Trash2,
    CheckCircle2, Clock, XCircle, ExternalLink, FileDown, Zap,
    BookOpen, Eye, Reply,
} from "lucide-react";
import { Link } from "react-router-dom";
import ShareAnalytics from "@/components/ShareAnalytics";

// Detect a public-share token in a result_link and return {token, kind}, or null.
const detectShareToken = (link) => {
    if (!link) return null;
    const mFirm = link.match(/\/p\/firm\/([a-f0-9]{8,})/i);
    if (mFirm) return { token: mFirm[1], kind: "firm" };
    const mPitch = link.match(/\/p\/([a-f0-9]{8,})/i);
    if (mPitch) return { token: mPitch[1], kind: "pitch" };
    return null;
};

// Group requests by batch_id. Preserves the order of the FIRST row seen in each group,
// so the inbox keeps the original sort. Single (non-batch) rows render as solo groups.
const groupByBatch = (rows) => {
    const groups = [];
    const indexByBatch = new Map();
    for (const r of rows) {
        if (!r.batch_id) {
            groups.push({ batch_id: null, rows: [r] });
            continue;
        }
        if (!indexByBatch.has(r.batch_id)) {
            indexByBatch.set(r.batch_id, groups.length);
            groups.push({ batch_id: r.batch_id, rows: [] });
        }
        groups[indexByBatch.get(r.batch_id)].rows.push(r);
    }
    return groups;
};

// Compact money formatter — $9.5M / $850K / $1.2B
const fmtPipelineMoney = (n) => {
    if (!n || n <= 0) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
    return `$${Math.round(n)}`;
};

// Configuration for each of the 5 request types. Keeps the form schema declarative.
const REQUEST_TYPES = [
    {
        key: "research",
        label: "Firm research",
        icon: Sparkles,
        tone: "border-amber-500/40 bg-amber-500/10 text-amber-300",
        desc: "Get a full Claude dossier on any company — products, financials, themes, news.",
        fields: [{ name: "firm_name", label: "Firm name", placeholder: "e.g. Stripe, SAP, Adyen" }],
    },
    {
        key: "synergy",
        label: "Partnership synergy",
        icon: Target,
        tone: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
        desc: "Score how well two firms would partner. Get pitch angles + revenue projection.",
        fields: [
            { name: "firm_a_name", label: "Source firm (selling)", placeholder: "e.g. Stripe" },
            { name: "firm_b_name", label: "Target firm", placeholder: "e.g. Shopify" },
            { name: "product_hint", label: "Specific product", placeholder: "e.g. Stripe Connect", optional: true },
        ],
    },
    {
        key: "stretch",
        label: "Stretch partners",
        icon: TrendingUp,
        tone: "border-cyan-500/40 bg-cyan-500/10 text-cyan-300",
        desc: "Find companies that would say YES to you with specific changes.",
        fields: [{ name: "firm_name", label: "Your firm name", placeholder: "Which firm to find stretch partners for" }],
    },
    {
        key: "stakeholders",
        label: "Stakeholder map",
        icon: Users,
        tone: "border-blue-500/40 bg-blue-500/10 text-blue-300",
        desc: "Identify the 5 key roles to engage at a target firm + LinkedIn search links.",
        fields: [{ name: "firm_name", label: "Target firm", placeholder: "e.g. SAP" }],
    },
    {
        key: "email_sequence",
        label: "5-email cadence",
        icon: Mail,
        tone: "border-purple-500/40 bg-purple-500/10 text-purple-300",
        desc: "Pre-written 14-day BD email sequence: value → proof → ROI → breakup → re-engage.",
        fields: [
            { name: "firm_a_name", label: "Your firm (sender)", placeholder: "e.g. Stripe" },
            { name: "firm_b_name", label: "Target firm", placeholder: "e.g. Adyen" },
            { name: "product_hint", label: "Product to pitch", placeholder: "e.g. Stripe Connect", optional: true },
        ],
    },
];

const STATUS_META = {
    pending: { label: "Pending", icon: Clock, tone: "border-zinc-700 bg-zinc-900 text-zinc-300" },
    in_progress: { label: "In progress", icon: Sparkles, tone: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
    completed: { label: "Completed", icon: CheckCircle2, tone: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
    declined: { label: "Declined", icon: XCircle, tone: "border-red-500/40 bg-red-500/10 text-red-300" },
};

export default function Requests() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showNew, setShowNew] = useState(false);
    // Bulk fulfill state (admin only). Map<requestId, true> for selected rows + progress chip.
    const [selected, setSelected] = useState({});
    const [bulkRunning, setBulkRunning] = useState({ active: false, done: 0, total: 0, currentName: "" });
    // Admin can flip between list (inbox-style) and pipeline (kanban) views
    const [viewMode, setViewMode] = useState("list");
    const [bulkError, setBulkError] = useState("");

    const load = async () => {
        setLoading(true); setError("");
        try {
            const { data } = await api.get("/requests");
            setRows(data.requests || []);
        } catch (e) {
            setError(rateLimitMessage(e) || "Failed to load requests");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    // Auto-fulfill is supported for 1-firm-payload types: research, stretch, stakeholders.
    const BULK_SUPPORTED = new Set(["research", "stretch", "stakeholders"]);
    const eligibleRows = (rows || []).filter((r) => BULK_SUPPORTED.has(r.type) && r.status !== "completed" && r.status !== "in_progress");
    const selectedCount = Object.values(selected).filter(Boolean).length;
    const selectedIds = Object.keys(selected).filter((id) => selected[id]);

    const toggleAll = () => {
        if (selectedCount === eligibleRows.length && selectedCount > 0) {
            setSelected({});
        } else {
            const next = {};
            for (const r of eligibleRows) next[r.id] = true;
            setSelected(next);
        }
    };

    // Run auto-fulfill sequentially across selected rows. Sequential = respects rate-limit (5/min)
    // and avoids saturating Claude with 10 parallel requests.
    const bulkFulfill = async () => {
        if (selectedCount === 0 || bulkRunning.active) return;
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Auto-fulfill ${selectedCount} request${selectedCount === 1 ? "" : "s"} sequentially? Each runs Claude (~30-60s per request).`)) return;
        setBulkError("");
        setBulkRunning({ active: true, done: 0, total: selectedIds.length, currentName: "" });
        for (let i = 0; i < selectedIds.length; i++) {
            const id = selectedIds[i];
            const row = (rows || []).find((r) => r.id === id);
            const name = row?.payload?.firm_name || id.slice(0, 8);
            setBulkRunning({ active: true, done: i, total: selectedIds.length, currentName: name });
            try {
                const { data: enq } = await api.post(`/requests/${id}/auto-fulfill`, {});
                await pollJob(enq.job_id, { timeoutMs: 5 * 60 * 1000 });
            } catch (e) {
                const rl = rateLimitMessage(e);
                const d = e?.response?.data?.detail;
                setBulkError(rl || (typeof d === "string" ? d : (e.jobError ? e.message : `Failed on ${name}`)));
                break; // short-circuit on first failure
            }
        }
        setBulkRunning({ active: false, done: 0, total: 0, currentName: "" });
        setSelected({});
        await load();
    };

    return (
        <div className="p-8" data-testid="requests-page">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// {isAdmin ? "Inbox" : "My requests"}</div>
                    <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">
                        {isAdmin ? "Client request inbox" : "Submit a request"}
                    </h1>
                    <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
                        {isAdmin
                            ? "All client requests across your workspace. Click any row to fulfill — attach a link and notes."
                            : "Send a research, synergy, or outreach request. Our AI analyst delivers a structured report in 2–20 minutes — we'll email you when it's ready."}
                    </p>
                </div>
                {!isAdmin && (
                    <button
                        onClick={() => setShowNew((v) => !v)}
                        data-testid="new-request-btn"
                        className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs uppercase tracking-wider px-5 py-3"
                    >
                        <Plus className="w-4 h-4" /> {showNew ? "Cancel" : "New request"}
                    </button>
                )}
                {isAdmin && <ReplyRateBadge refreshKey={rows?.length || 0} />}
            </div>

            {error && <div className="border border-red-900/60 bg-red-950/30 text-red-400 text-sm px-4 py-3 font-mono mb-6">{error}</div>}

            {/* Client dashboard — only on client view, only when we have data loaded */}
            {!isAdmin && rows && <ClientDashboard rows={rows} user={user} onNewRequest={() => setShowNew(true)} />}

            {/* View toggle — admin only. Inbox list ↔ kanban pipeline */}
            {isAdmin && rows && rows.length > 0 && (
                <div className="inline-flex border border-zinc-800 mb-4" data-testid="view-toggle">
                    <button
                        onClick={() => setViewMode("list")}
                        data-testid="view-mode-list"
                        className={`text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 transition-colors ${
                            viewMode === "list" ? "bg-amber-500/15 text-amber-300" : "text-zinc-500 hover:text-zinc-200"
                        }`}
                    >
                        Inbox
                    </button>
                    <button
                        onClick={() => setViewMode("pipeline")}
                        data-testid="view-mode-pipeline"
                        className={`text-[10px] font-mono uppercase tracking-wider px-3 py-1.5 border-l border-zinc-800 transition-colors ${
                            viewMode === "pipeline" ? "bg-amber-500/15 text-amber-300" : "text-zinc-500 hover:text-zinc-200"
                        }`}
                    >
                        Pipeline
                    </button>
                </div>
            )}

            {/* Bulk fulfill banner — admin only, only when eligible rows exist */}
            {isAdmin && eligibleRows.length > 0 && (
                <div className="border border-amber-500/40 bg-amber-500/5 px-4 py-3 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="bulk-fulfill-banner">
                    <div className="flex items-center gap-3 text-xs text-zinc-300 font-mono flex-wrap">
                        {bulkRunning.active ? (
                            <><span className="text-amber-400">Running</span> {bulkRunning.done + 1}/{bulkRunning.total} — <span className="text-zinc-100">{bulkRunning.currentName}</span>…</>
                        ) : (
                            <>
                                <button
                                    onClick={toggleAll}
                                    data-testid="bulk-toggle-all"
                                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-300 hover:text-amber-400 border border-zinc-800 hover:border-amber-500 px-2 py-1 transition-colors"
                                >
                                    {selectedCount === eligibleRows.length && selectedCount > 0 ? "Deselect all" : `Select all ${eligibleRows.length} eligible`}
                                </button>
                                <span className="text-zinc-500">
                                    {selectedCount > 0 ? `${selectedCount} selected · sequential AI run (~30-60s each)` : "Tick boxes on pending research/stretch/stakeholder rows to bulk-fulfill"}
                                </span>
                            </>
                        )}
                    </div>
                    <button
                        onClick={bulkFulfill}
                        disabled={bulkRunning.active || selectedCount === 0}
                        data-testid="bulk-fulfill-btn"
                        className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold text-[11px] uppercase tracking-wider px-3.5 py-2"
                    >
                        <Zap className="w-3.5 h-3.5" /> {bulkRunning.active ? `Running ${bulkRunning.done + 1}/${bulkRunning.total}…` : `Auto-fulfill ${selectedCount}`}
                    </button>
                </div>
            )}
            {bulkError && <div className="border border-red-900/60 bg-red-950/30 text-red-400 text-sm px-4 py-3 font-mono mb-4">{bulkError}</div>}

            {showNew && !isAdmin && (
                <NewRequestForm
                    onClose={() => setShowNew(false)}
                    onCreated={() => { setShowNew(false); load(); }}
                />
            )}

            {loading && !rows && <Loader label="Loading requests" />}

            {rows && rows.length === 0 && (
                <div className="border border-dashed border-zinc-800 p-12 text-center">
                    <Inbox className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest">
                        {isAdmin ? "No requests yet" : "You haven't submitted anything yet"}
                    </div>
                    {!isAdmin && (
                        <button
                            onClick={() => setShowNew(true)}
                            className="mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-amber-500 hover:text-amber-400"
                        >
                            <Plus className="w-3 h-3" /> Submit your first request
                        </button>
                    )}
                </div>
            )}

            {rows && rows.length > 0 && (isAdmin && viewMode === "pipeline" ? (
                <RequestPipeline rows={rows} onChange={load} />
            ) : (
                <div className="space-y-3" data-testid="requests-list">
                    {groupByBatch(rows).map((group) => {
                        // Single (non-batch) row — render directly
                        if (!group.batch_id) {
                            const r = group.rows[0];
                            return (
                                <RequestRow
                                    key={r.id}
                                    r={r}
                                    isAdmin={isAdmin}
                                    onChange={load}
                                    selectable={isAdmin && BULK_SUPPORTED.has(r.type) && r.status !== "completed" && r.status !== "in_progress"}
                                    selected={!!selected[r.id]}
                                    onSelectChange={(checked) => setSelected((s) => ({ ...s, [r.id]: checked }))}
                                    bulkLocked={bulkRunning.active}
                                />
                            );
                        }
                        // Batch group — colored frame + header with quick "select all in batch"
                        const eligibleInBatch = group.rows.filter((r) => BULK_SUPPORTED.has(r.type) && r.status !== "completed" && r.status !== "in_progress");
                        const selectedInBatch = eligibleInBatch.filter((r) => selected[r.id]).length;
                        const selectAllInBatch = () => {
                            const next = { ...selected };
                            const turnOn = selectedInBatch < eligibleInBatch.length;
                            for (const r of eligibleInBatch) next[r.id] = turnOn;
                            setSelected(next);
                        };
                        const allDone = group.rows.every((r) => r.status === "completed");
                        const firstRow = group.rows[0];
                        return (
                            <div key={group.batch_id} className="border-l-2 border-amber-500/60 pl-3" data-testid={`batch-group-${group.batch_id}`}>
                                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500">
                                        Batch · {group.rows.length} tools{!isAdmin && firstRow.client_name ? "" : ""} {allDone && <span className="ml-1 text-emerald-400">· all done ✓</span>}
                                    </div>
                                    {isAdmin && eligibleInBatch.length > 0 && (
                                        <button
                                            onClick={selectAllInBatch}
                                            disabled={bulkRunning.active}
                                            data-testid={`batch-select-${group.batch_id}`}
                                            className="text-[10px] font-mono uppercase tracking-wider border border-zinc-800 hover:border-amber-500 hover:text-amber-400 text-zinc-400 px-2 py-0.5 disabled:opacity-50"
                                        >
                                            {selectedInBatch === eligibleInBatch.length ? "Deselect batch" : `Select ${eligibleInBatch.length} in batch`}
                                        </button>
                                    )}
                                </div>
                                <div className="space-y-3">
                                    {group.rows.map((r) => (
                                        <RequestRow
                                            key={r.id}
                                            r={r}
                                            isAdmin={isAdmin}
                                            onChange={load}
                                            selectable={isAdmin && BULK_SUPPORTED.has(r.type) && r.status !== "completed" && r.status !== "in_progress"}
                                            selected={!!selected[r.id]}
                                            onSelectChange={(checked) => setSelected((s) => ({ ...s, [r.id]: checked }))}
                                            bulkLocked={bulkRunning.active}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
}

// Single row in the requests list. Expands on click to show the fulfillment workflow (admin)
// or the result detail (client).
function RequestRow({ r, isAdmin, onChange, selectable = false, selected = false, onSelectChange = () => {}, bulkLocked = false }) {
    const meta = REQUEST_TYPES.find((t) => t.key === r.type) || REQUEST_TYPES[0];
    const Icon = meta.icon;
    const status = STATUS_META[r.status] || STATUS_META.pending;
    const StatusIcon = status.icon;
    const [expanded, setExpanded] = useState(false);
    const payloadPreview = Object.entries(r.payload || {})
        .filter(([_, v]) => String(v).trim())
        .map(([k, v]) => `${k.replace("_", " ")}: ${v}`)
        .join(" · ");

    return (
        <article className="border border-zinc-800 bg-zinc-950" data-testid={`request-row-${r.id}`}>
            <div className="flex items-stretch">
                {selectable && (
                    <label
                        className="flex items-center justify-center px-3 border-r border-zinc-800 cursor-pointer hover:bg-zinc-900/60 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`select-${r.id}`}
                    >
                        <input
                            type="checkbox"
                            checked={selected}
                            disabled={bulkLocked}
                            onChange={(e) => onSelectChange(e.target.checked)}
                            className="accent-amber-500 w-4 h-4 cursor-pointer disabled:opacity-50"
                        />
                    </label>
                )}
                <button
                    onClick={() => setExpanded((v) => !v)}
                    className="flex-1 text-left p-5 flex items-start justify-between gap-4 hover:bg-zinc-900/30 transition-colors"
                    data-testid={`request-toggle-${r.id}`}
                >
                    <div className="flex items-start gap-4 min-w-0 flex-1">
                        <div className="w-10 h-10 border border-zinc-800 flex items-center justify-center shrink-0">
                            <Icon className="w-4 h-4 text-amber-500" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className={`inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${meta.tone}`}>{meta.label}</span>
                                <span className={`inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${status.tone}`}>
                                    <StatusIcon className="w-3 h-3" /> {status.label}
                                </span>
                                {r.want_pdf && (
                                    <span className="inline-flex items-center gap-1 border border-zinc-700 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-zinc-400">
                                        <FileDown className="w-3 h-3" /> PDF
                                    </span>
                                )}
                                {r.replied_at && (
                                    <span className="inline-flex items-center gap-1 border border-emerald-500/50 bg-emerald-500/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-300" data-testid={`replied-badge-${r.id}`} title={`Replied ${new Date(r.replied_at).toLocaleString()}`}>
                                        <Reply className="w-3 h-3" /> Replied
                                    </span>
                                )}
                                {isAdmin && r.status === "completed" && (
                                    <EmailQueueChip request={r} onChange={onChange} />
                                )}
                                {!isAdmin && r.auto_fulfill && (
                                    <AutoFulfillEtaChip request={r} onChange={onChange} />
                                )}
                            </div>
                            <div className="text-sm text-zinc-200 mt-1.5 truncate">{payloadPreview || "(no details)"}</div>
                            {isAdmin && (
                                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-1">
                                    FROM {r.client_email || r.client_name || r.client_id}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="font-mono text-[10px] text-zinc-500 shrink-0">{new Date(r.created_at).toLocaleDateString()}</div>
                </button>
            </div>
            {expanded && (
                <RequestDetail r={r} isAdmin={isAdmin} onChange={onChange} onClose={() => setExpanded(false)} />
            )}
        </article>
    );
}

// Detail panel for an expanded request — shows admin fulfillment form OR client read-only view.
function RequestDetail({ r, isAdmin, onChange, onClose }) {
    const [status, setStatus] = useState(r.status);
    const [link, setLink] = useState(r.result_link || "");
    const [notes, setNotes] = useState(r.result_notes || "");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [autoRunning, setAutoRunning] = useState(false);
    const [autoPublic, setAutoPublic] = useState(false); // research-only: also mint public dossier URL

    // Auto-fulfill supports research / stretch / stakeholders (1-firm payloads).
    const autoFulfillTypes = ["research", "stretch", "stakeholders"];
    const canAutoFulfill = autoFulfillTypes.includes(r.type) && r.status !== "completed";

    const autoFulfill = async () => {
        const firmName = r.payload?.firm_name || "(this firm)";
        const verb = r.type === "research" ? "researching" : r.type === "stretch" ? "finding stretch partners for" : "mapping stakeholders at";
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Auto-fulfill this request? Will run Claude ${verb} "${firmName}"${r.type === "research" && autoPublic ? " and mint a public dossier URL" : ""}.`)) return;
        setAutoRunning(true); setErr("");
        try {
            const { data: enq } = await api.post(`/requests/${r.id}/auto-fulfill`, r.type === "research" ? { with_public_link: autoPublic } : {});
            await pollJob(enq.job_id, { timeoutMs: 5 * 60 * 1000 });
            onChange();
            onClose();
        } catch (e) {
            const rl = rateLimitMessage(e);
            const d = e?.response?.data?.detail;
            setErr(rl || (typeof d === "string" ? d : (e.jobError ? e.message : "Auto-fulfill failed")));
        } finally {
            setAutoRunning(false);
        }
    };

    const save = async () => {
        setSaving(true); setErr("");
        try {
            await api.patch(`/requests/${r.id}`, { status, result_link: link, result_notes: notes });
            onChange();
            onClose();
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "string" ? d : "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const del = async () => {
        // eslint-disable-next-line no-alert
        if (!window.confirm("Delete this request?")) return;
        try {
            await api.delete(`/requests/${r.id}`);
            onChange();
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "string" ? d : "Delete failed");
        }
    };

    return (
        <div className="border-t border-zinc-800 bg-black p-5" data-testid={`request-detail-${r.id}`}>
            {r.notes && (
                <div className="mb-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Client notes</div>
                    <div className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{r.notes}</div>
                </div>
            )}

            {isAdmin ? (
                <div className="space-y-4" data-testid={`admin-fulfill-${r.id}`}>
                    {/* Auto-fulfill banner — research/stretch/stakeholders */}
                    {canAutoFulfill && (
                        <div className="border border-amber-500/40 bg-amber-500/5 px-4 py-3 space-y-3" data-testid={`auto-fulfill-banner-${r.id}`}>
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="text-xs text-zinc-300 font-mono leading-relaxed">
                                    {autoRunning ? (
                                        <><span className="text-amber-400">Running Claude</span> — {r.type === "research" ? "researching" : r.type === "stretch" ? "finding stretch partners" : "mapping stakeholders"}, saving to library, formatting the deliverable (~30-60s)…</>
                                    ) : (
                                        <>One-click: run Claude on <span className="text-amber-400">{r.payload?.firm_name || "this firm"}</span> and attach the {r.type === "research" ? "dossier" : r.type === "stretch" ? "stretch-partner list" : "stakeholder map"} as the client deliverable.</>
                                    )}
                                </div>
                                <button
                                    onClick={autoFulfill}
                                    disabled={autoRunning || saving}
                                    data-testid={`auto-fulfill-btn-${r.id}`}
                                    className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-wait text-black font-bold text-[11px] uppercase tracking-wider px-3.5 py-2"
                                >
                                    <Zap className="w-3.5 h-3.5" /> {autoRunning ? "Running…" : "Auto-fulfill"}
                                </button>
                            </div>
                            {/* Research-only: optional public dossier URL — the client can forward this to prospects */}
                            {r.type === "research" && (
                                <label className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-zinc-400 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={autoPublic}
                                        onChange={(e) => setAutoPublic(e.target.checked)}
                                        disabled={autoRunning}
                                        data-testid={`auto-public-checkbox-${r.id}`}
                                        className="accent-amber-500"
                                    />
                                    Also mint a public dossier URL the client can forward to prospects
                                </label>
                            )}
                        </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
                        <div className="md:col-span-1">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Status</div>
                            <select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                data-testid={`status-select-${r.id}`}
                                className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm font-mono px-3 py-2"
                            >
                                {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                            </select>
                        </div>
                        <div className="md:col-span-3">
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Result link (public pitch URL, firm dossier, etc.)</div>
                            <input
                                value={link}
                                onChange={(e) => setLink(e.target.value)}
                                placeholder="https://affynix.it/p/abc123…"
                                data-testid={`result-link-${r.id}`}
                                className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm px-3 py-2"
                            />
                            {(() => {
                                const tk = detectShareToken(link);
                                if (!tk) return null;
                                return (
                                    <div className="mt-1.5" data-testid={`admin-engagement-${r.id}`}>
                                        <ShareAnalytics token={tk.token} kind={tk.kind} compact />
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Result notes (markdown ok)</div>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={6}
                            placeholder="Paste the AI output, a summary, or a follow-up note for the client."
                            data-testid={`result-notes-${r.id}`}
                            className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm px-3 py-2 leading-relaxed"
                        />
                    </div>
                    {err && <div className="text-xs text-red-400 font-mono">{err}</div>}
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            onClick={save}
                            disabled={saving}
                            data-testid={`save-fulfill-${r.id}`}
                            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-bold text-xs uppercase tracking-wider px-4 py-2"
                        >
                            <Send className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save & deliver"}
                        </button>
                        {r.status === "completed" && r.result_link && (
                            <button
                                onClick={async () => {
                                    try {
                                        await api.post(`/requests/${r.id}/mark-replied`);
                                        onChange();
                                    } catch { /* noop */ }
                                }}
                                data-testid={`mark-replied-${r.id}`}
                                title={r.replied_at ? `Replied ${new Date(r.replied_at).toLocaleString()} — click to undo` : "Prospect emailed back? Tap to log it."}
                                className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-3 py-2 border transition-colors ${
                                    r.replied_at
                                        ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                                        : "border-zinc-800 hover:border-emerald-500/60 hover:text-emerald-400 text-zinc-400"
                                }`}
                            >
                                <Reply className="w-3 h-3" /> {r.replied_at ? "Replied ✓" : "Mark replied"}
                            </button>
                        )}
                        <button
                            onClick={del}
                            data-testid={`delete-req-${r.id}`}
                            className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-red-500 hover:text-red-400 text-zinc-500 text-[10px] font-mono uppercase tracking-wider px-3 py-2 transition-colors"
                        >
                            <Trash2 className="w-3 h-3" /> Delete
                        </button>
                    </div>
                </div>
            ) : (
                <div className="space-y-3" data-testid={`client-view-${r.id}`}>
                    {r.result_link && (() => {
                        const isPublic = r.result_link.startsWith("/p/") || r.result_link.startsWith("http");
                        const href = r.result_link.startsWith("/") ? `${window.location.origin}${r.result_link}` : r.result_link;
                        return (
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400 mb-1">
                                    {isPublic ? "Your deliverable (shareable link)" : "Your deliverable"}
                                </div>
                                {isPublic ? (
                                    <a
                                        href={href}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`client-result-link-${r.id}`}
                                        className="inline-flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/10 px-3 py-2 font-mono text-xs break-all"
                                    >
                                        <ExternalLink className="w-3 h-3 shrink-0" /> {href}
                                    </a>
                                ) : (
                                    <div className="font-mono text-[11px] text-zinc-500" data-testid={`client-result-link-${r.id}`}>
                                        Internal reference: {r.result_link}
                                    </div>
                                )}
                                {(() => {
                                    const tk = detectShareToken(r.result_link);
                                    if (!tk) return null;
                                    return (
                                        <div className="mt-3" data-testid={`client-engagement-${r.id}`}>
                                            <ShareAnalytics token={tk.token} kind={tk.kind} />
                                        </div>
                                    );
                                })()}
                            </div>
                        );
                    })()}
                    {r.result_notes && (
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Analyst notes</div>
                            <div className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap border border-zinc-800 bg-zinc-950 p-3" data-testid={`client-result-notes-${r.id}`}>{r.result_notes}</div>
                        </div>
                    )}
                    {!r.result_link && !r.result_notes && (
                        <div className="text-xs text-zinc-500 font-mono">
                            {r.status === "completed" ? "Marked completed but no notes attached. Reach out to your analyst." : "Waiting for analyst delivery…"}
                        </div>
                    )}
                    {r.status === "pending" && (
                        <button
                            onClick={del}
                            data-testid={`cancel-req-${r.id}`}
                            className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-red-500 hover:text-red-400 text-zinc-500 text-[10px] font-mono uppercase tracking-wider px-3 py-2 transition-colors"
                        >
                            <Trash2 className="w-3 h-3" /> Cancel request
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

// Submission form. Clients pick ONE OR MORE tools. Each ticked tool spawns one independent
// request — they share the form's notes + PDF flag + collected input fields.
function NewRequestForm({ onClose, onCreated }) {
    const { user } = useAuth();
    // Multi-select: Set of REQUEST_TYPES keys
    const [selectedTypes, setSelectedTypes] = useState(() => new Set([REQUEST_TYPES[0].key]));
    const [values, setValues] = useState({});
    const [notes, setNotes] = useState("");
    const [wantPdf, setWantPdf] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");
    const [progress, setProgress] = useState({ done: 0, total: 0, currentLabel: "" });
    const [limitInfo, setLimitInfo] = useState(null);  // 402 daily-limit payload from backend

    const selectedCfgs = REQUEST_TYPES.filter((t) => selectedTypes.has(t.key));

    // Union all fields across selected tools, dedup by name. Preserves first-seen order.
    const allFields = (() => {
        const seen = new Set();
        const out = [];
        for (const cfg of selectedCfgs) {
            for (const f of cfg.fields) {
                if (!seen.has(f.name)) {
                    seen.add(f.name);
                    out.push(f);
                }
            }
        }
        return out;
    })();

    const toggleType = (key) => {
        setSelectedTypes((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                if (next.size === 1) return prev; // require at least one
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    // Validate: every REQUIRED field across selected tools must be filled.
    const missingRequired = allFields.filter((f) => !f.optional && !(values[f.name] || "").trim());
    const canSubmit = selectedTypes.size > 0 && missingRequired.length === 0;

    const submit = async () => {
        if (!canSubmit || submitting) return;
        setSubmitting(true); setErr("");
        const cfgsToSubmit = [...selectedCfgs];
        setProgress({ done: 0, total: cfgsToSubmit.length, currentLabel: "" });
        // Shared batch ID — only when submitting >1 tool. Admin inbox groups rows by this.
        const batchId = cfgsToSubmit.length > 1
            ? (window.crypto?.randomUUID?.() || `batch_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`)
            : null;
        try {
            for (let i = 0; i < cfgsToSubmit.length; i++) {
                const cfg = cfgsToSubmit[i];
                setProgress({ done: i, total: cfgsToSubmit.length, currentLabel: cfg.label });
                const payload = {};
                for (const f of cfg.fields) {
                    const v = (values[f.name] || "").trim();
                    if (v) payload[f.name] = v;
                }
                await api.post("/requests", { type: cfg.key, payload, notes, want_pdf: wantPdf, batch_id: batchId });
            }
            setProgress((p) => ({ ...p, done: p.total }));
            onCreated();
        } catch (e) {
            const d = e?.response?.data?.detail;
            // Daily-limit hit (402) returns a structured object with a CODE we can switch on.
            if (e?.response?.status === 402 && d?.code === "DAILY_LIMIT_REACHED") {
                setLimitInfo(d);
                setErr("");  // limitInfo banner replaces the plain err line
            } else {
                setErr(typeof d === "string" ? d : `Submission failed at "${progress.currentLabel || "?"}"`);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="border border-amber-500/40 bg-amber-500/[0.03] p-5 mb-6" data-testid="new-request-form">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500">// New request — pick one or more</div>
                <div className="font-mono text-[10px] text-zinc-500" data-testid="type-counter">
                    {selectedTypes.size} tool{selectedTypes.size === 1 ? "" : "s"} selected
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4" data-testid="type-picker">
                {REQUEST_TYPES.map((t) => {
                    const Icon = t.icon;
                    const active = selectedTypes.has(t.key);
                    return (
                        <button
                            key={t.key}
                            onClick={() => toggleType(t.key)}
                            data-testid={`type-${t.key}`}
                            aria-pressed={active}
                            className={`relative flex flex-col items-start gap-1 p-3 border transition-colors text-left ${
                                active ? "border-amber-500 bg-amber-500/10" : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"
                            }`}
                        >
                            <div className="flex items-center gap-1.5 w-full">
                                <Icon className={`w-4 h-4 ${active ? "text-amber-400" : "text-zinc-500"}`} />
                                {active && (
                                    <span className="ml-auto inline-flex items-center justify-center w-4 h-4 bg-amber-500 text-black" data-testid={`type-${t.key}-checked`}>
                                        <CheckCircle2 className="w-3 h-3" />
                                    </span>
                                )}
                            </div>
                            <div className={`font-heading text-xs leading-tight ${active ? "text-zinc-50" : "text-zinc-300"}`}>{t.label}</div>
                        </button>
                    );
                })}
            </div>

            {/* Per-tool one-line descriptions for selected tools */}
            {selectedCfgs.length > 0 && (
                <div className="border border-zinc-800 bg-black p-3 mb-4 space-y-1.5" data-testid="selected-descriptions">
                    {selectedCfgs.map((cfg) => {
                        const Icon = cfg.icon;
                        return (
                            <div key={cfg.key} className="flex items-start gap-2 text-xs text-zinc-400 leading-relaxed">
                                <Icon className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />
                                <div><span className="text-zinc-200 font-semibold">{cfg.label}:</span> {cfg.desc}</div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="space-y-3">
                {allFields.map((f) => (
                    <div key={f.name}>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
                            {f.label}{f.optional ? " (optional)" : ""}
                        </div>
                        <input
                            value={values[f.name] || ""}
                            onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                            placeholder={f.placeholder}
                            data-testid={`field-${f.name}`}
                            className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm px-3 py-2"
                        />
                    </div>
                ))}
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Context / notes (optional, shared across all selected tools)</div>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        placeholder="Anything specific the analyst should know — urgency, target audience, focus areas…"
                        data-testid="field-notes"
                        className="w-full bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm px-3 py-2 leading-relaxed"
                    />
                </div>
                <label className="inline-flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={wantPdf}
                        onChange={(e) => setWantPdf(e.target.checked)}
                        data-testid="want-pdf-checkbox"
                        className="accent-amber-500"
                    />
                    <FileDown className="w-3 h-3" /> Also include a PDF deliverable
                </label>
            </div>
            {err && <div className="text-xs text-red-400 font-mono mt-3" data-testid="submit-error">{err}</div>}
            {missingRequired.length > 0 && (
                <div className="text-[10px] text-zinc-500 font-mono mt-3" data-testid="missing-required">
                    Fill in: {missingRequired.map((f) => f.label).join(", ")}
                </div>
            )}
            {/* Daily-limit upsell banner — only shown after a 402 from the backend. Replaces
                the normal error line with two clear actions: buy a one-off OR wait. */}
            {limitInfo && (
                <div
                    className="mt-4 border border-amber-500/40 bg-amber-500/[0.08] p-4"
                    data-testid="daily-limit-banner"
                >
                    <div className="flex items-start gap-3">
                        <Zap className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                        <div className="text-sm leading-relaxed flex-1">
                            <div className="text-amber-300 font-semibold tracking-wide">Daily Fast-Lane limit reached.</div>
                            <div className="text-zinc-400 mt-1">
                                You've used your {limitInfo.limit} Fast-Lane research deliveries today.
                                Resets at <span className="font-mono text-zinc-200">{new Date(limitInfo.resets_at).toLocaleString()}</span>.
                            </div>
                            <div className="flex gap-2 mt-3 flex-wrap">
                                <Link
                                    to="/app/upgrade"
                                    data-testid="upsell-buy-oneoff-btn"
                                    className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs uppercase tracking-wider px-4 py-2"
                                >
                                    Buy €64 Express or €59 Standard →
                                </Link>
                                <button
                                    onClick={() => setLimitInfo(null)}
                                    data-testid="upsell-wait-btn"
                                    className="inline-flex items-center gap-2 border border-zinc-700 hover:border-zinc-500 text-zinc-300 hover:text-zinc-100 font-mono text-xs uppercase tracking-wider px-4 py-2"
                                >
                                    Wait until tomorrow
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Email-notification notice — sets the expectation that the customer will be
                pinged by email when the AI analyst finishes (no need to refresh /app/requests).
                Pro-aware: shows "instant Pro lane" vs the 2-20 min queue copy. */}
            {user?.tier === "pro" ? (
                <div
                    className="mt-4 border border-amber-500/40 bg-amber-500/[0.06] p-3 flex items-start gap-3"
                    data-testid="email-notify-notice-pro"
                >
                    <Zap className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                    <div className="text-xs leading-relaxed">
                        <div className="text-amber-300 font-semibold tracking-wide">⚡ Pro Lane — your research skips the queue.</div>
                        <div className="text-zinc-400 mt-0.5">
                            Your AI analyst will start drafting <span className="text-amber-400 font-mono">within 60 seconds</span>.
                            We'll send the finished research to{" "}
                            <span className="text-zinc-200 font-mono" data-testid="notify-email-target">{user?.email || "your account email"}</span>{" "}
                            — no need to keep this tab open.
                        </div>
                    </div>
                </div>
            ) : (
                <div
                    className="mt-4 border border-emerald-500/30 bg-emerald-500/[0.04] p-3 flex items-start gap-3"
                    data-testid="email-notify-notice"
                >
                    <Mail className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    <div className="text-xs leading-relaxed">
                        <div className="text-emerald-300 font-semibold tracking-wide">We'll email you when it's ready.</div>
                        <div className="text-zinc-400 mt-0.5">
                            Your AI analyst typically delivers in <span className="text-amber-400 font-mono">2–20 minutes</span>.
                            We'll send the finished report to{" "}
                            <span className="text-zinc-200 font-mono" data-testid="notify-email-target">{user?.email || "your account email"}</span>{" "}
                            — no need to keep this tab open.
                        </div>
                    </div>
                </div>
            )}
            <div className="flex items-center gap-3 mt-4 flex-wrap">
                <button
                    onClick={submit}
                    disabled={submitting || !canSubmit}
                    data-testid="submit-request-btn"
                    className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold text-xs uppercase tracking-wider px-5 py-2.5"
                >
                    <Send className="w-3.5 h-3.5" />
                    {submitting
                        ? `Submitting ${progress.done + 1}/${progress.total}…`
                        : `Submit ${selectedTypes.size} request${selectedTypes.size === 1 ? "" : "s"}`}
                </button>
                {submitting && progress.currentLabel && (
                    <span className="text-[10px] text-zinc-500 font-mono" data-testid="submit-progress">
                        Sending: {progress.currentLabel}…
                    </span>
                )}
                <button
                    onClick={onClose}
                    disabled={submitting}
                    className="text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-zinc-300 px-3 py-2"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
}

// Client onboarding + at-a-glance dashboard. Lives at the top of /app/requests for client role.
function ClientDashboard({ rows, user, onNewRequest }) {
    const stats = {
        total: rows.length,
        pending: rows.filter((r) => r.status === "pending").length,
        in_progress: rows.filter((r) => r.status === "in_progress").length,
        completed: rows.filter((r) => r.status === "completed").length,
    };
    // Detect completed requests that have shareable public links (deliverables ready)
    const deliverables = rows.filter((r) => r.status === "completed" && detectShareToken(r.result_link));
    const greeting = greetingForHour();
    const firstName = (user?.name || user?.email || "there").split(/[\s@]/)[0];

    return (
        <div className="mb-8 space-y-5" data-testid="client-dashboard">
            {/* Greeting + Stats */}
            <div className="border border-zinc-800 bg-zinc-950 p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-1">// {greeting}</div>
                        <div className="font-heading font-black text-2xl text-zinc-50" data-testid="client-greeting">Welcome back, {firstName}.</div>
                        <div className="text-sm text-zinc-400 mt-1">
                            {stats.total === 0
                                ? "Ready when you are — submit your first research request to get started."
                                : `You have ${stats.total} request${stats.total === 1 ? "" : "s"} on file.`}
                        </div>
                    </div>
                    {stats.total === 0 && (
                        <button
                            onClick={onNewRequest}
                            data-testid="client-empty-cta"
                            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs uppercase tracking-wider px-5 py-3"
                        >
                            <Plus className="w-4 h-4" /> Submit your first
                        </button>
                    )}
                </div>

                {stats.total > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="client-stats">
                        <StatCard label="Total" value={stats.total} tone="zinc" icon={<Inbox className="w-3.5 h-3.5" />} testid="stat-total" />
                        <StatCard label="Pending" value={stats.pending} tone={stats.pending > 0 ? "amber" : "zinc"} icon={<Clock className="w-3.5 h-3.5" />} testid="stat-pending" />
                        <StatCard label="In progress" value={stats.in_progress} tone={stats.in_progress > 0 ? "amber" : "zinc"} icon={<Sparkles className="w-3.5 h-3.5" />} testid="stat-in-progress" />
                        <StatCard label="Completed" value={stats.completed} tone={stats.completed > 0 ? "emerald" : "zinc"} icon={<CheckCircle2 className="w-3.5 h-3.5" />} testid="stat-completed" />
                    </div>
                )}
            </div>

            {/* Deliverables ready — public links the prospect can open */}
            {deliverables.length > 0 && (
                <div className="border border-emerald-500/30 bg-emerald-950/10 p-5" data-testid="client-deliverables">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400 mb-3 flex items-center gap-1.5">
                        <Eye className="w-3.5 h-3.5" /> Your live shareable links ({deliverables.length})
                    </div>
                    <div className="space-y-1.5">
                        {deliverables.slice(0, 4).map((r) => {
                            const tk = detectShareToken(r.result_link);
                            const isFirm = tk?.kind === "firm";
                            const href = r.result_link.startsWith("http") ? r.result_link : `${window.location.origin}${r.result_link}`;
                            return (
                                <a
                                    key={r.id}
                                    href={href}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid={`client-deliverable-${r.id}`}
                                    className="flex items-center justify-between gap-3 border border-zinc-800 hover:border-emerald-500/60 bg-black px-3 py-2 transition-colors group"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm text-zinc-100 truncate font-semibold">
                                            {r.payload?.firm_name || r.payload?.firm_a_name || "Request"} {r.payload?.firm_b_name && <span className="text-zinc-500">× {r.payload.firm_b_name}</span>}
                                        </div>
                                        <div className="font-mono text-[10px] text-zinc-500 truncate">{isFirm ? "Firm dossier" : "Partnership pitch"} · {href}</div>
                                    </div>
                                    <ExternalLink className="w-3.5 h-3.5 text-zinc-500 group-hover:text-emerald-400 shrink-0" />
                                </a>
                            );
                        })}
                    </div>
                    {deliverables.length > 4 && (
                        <div className="font-mono text-[10px] text-zinc-500 mt-2">+ {deliverables.length - 4} more below ↓</div>
                    )}
                </div>
            )}

            {/* How it works — only when there are no completed requests yet (onboarding) */}
            {stats.completed === 0 && (
                <div className="border border-zinc-800 bg-zinc-950 p-6" data-testid="client-how-it-works">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4 flex items-center gap-1.5">
                        <BookOpen className="w-3.5 h-3.5" /> How it works
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Step n={1} title="You submit" desc="Pick a request type (firm research, partnership synergy, outreach…) and tell us the targets." />
                        <Step n={2} title="We deliver" desc="Our AI analyst runs the full pipeline + verifies the output. Most requests land in 2–20 minutes." />
                        <Step n={3} title="You share" desc="Each deliverable gets a public link with live engagement analytics — see exactly who opens it." />
                    </div>
                </div>
            )}

            {/* Booking-URL nudge removed — outreach is strictly email-only. */}
        </div>
    );
}

function StatCard({ label, value, tone, icon, testid }) {
    const toneClass =
        tone === "emerald"
            ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
            : tone === "amber"
                ? "border-amber-500/40 text-amber-300 bg-amber-500/5"
                : "border-zinc-800 text-zinc-300";
    return (
        <div className={`border ${toneClass} px-3 py-2.5`} data-testid={testid}>
            <div className="flex items-center gap-1.5 text-zinc-500 mb-1">
                {icon}
                <span className="font-mono text-[9px] uppercase tracking-[0.2em]">{label}</span>
            </div>
            <div className="font-heading font-black text-2xl leading-none">{value}</div>
        </div>
    );
}

function Step({ n, title, desc }) {
    return (
        <div className="flex gap-3">
            <div className="shrink-0 w-7 h-7 rounded-sm border border-amber-500/40 bg-amber-500/10 font-heading font-black text-amber-400 flex items-center justify-center text-sm">{n}</div>
            <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-100 mb-0.5">{title}</div>
                <div className="text-xs text-zinc-400 leading-relaxed">{desc}</div>
            </div>
        </div>
    );
}

function greetingForHour() {
    const h = new Date().getHours();
    if (h < 5) return "Working late";
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    if (h < 22) return "Good evening";
    return "Working late";
}

// Kanban-style pipeline view of requests. 4 visible stages mapped from (status, replied_at):
//   pending → in_progress → completed (awaiting reply) → replied. Drag a card to move it.
// "Declined" is appended as a 5th compact archive column when any exist.
const PIPELINE_STAGES = [
    { key: "pending", label: "Pending", filter: (r) => r.status === "pending", tone: "border-zinc-700 text-zinc-300" },
    { key: "in_progress", label: "In progress", filter: (r) => r.status === "in_progress", tone: "border-amber-500/40 text-amber-300" },
    { key: "completed", label: "Delivered · awaiting reply", filter: (r) => r.status === "completed" && !r.replied_at, tone: "border-emerald-500/30 text-emerald-300" },
    { key: "replied", label: "Replied", filter: (r) => r.status === "completed" && r.replied_at, tone: "border-emerald-500/60 text-emerald-200" },
];

function RequestPipeline({ rows, onChange }) {
    const declined = rows.filter((r) => r.status === "declined");
    const [dragging, setDragging] = useState(null); // request id
    const [dragOver, setDragOver] = useState(null); // stage key
    const [forecast, setForecast] = useState(null);
    const [velocity, setVelocity] = useState(null);

    // Refetch the pipeline-value forecast whenever rows change (drag-drop triggers reload upstream).
    useEffect(() => {
        let cancelled = false;
        api.get("/requests/pipeline-value")
            .then(({ data }) => { if (!cancelled) setForecast(data); })
            .catch(() => { if (!cancelled) setForecast(null); });
        api.get("/requests/velocity")
            .then(({ data }) => { if (!cancelled) setVelocity(data); })
            .catch(() => { if (!cancelled) setVelocity(null); });
        return () => { cancelled = true; };
    }, [rows.length, rows]);

    const stageStats = (key) => forecast?.stages?.find((s) => s.key === key) || null;

    const moveTo = async (req, targetStage) => {
        // Compute the patch needed to land in the target stage.
        // status transitions handled via PATCH /requests/{id}; replied flag via mark-replied toggle.
        try {
            const isCurrentlyReplied = !!req.replied_at;
            if (targetStage === "replied") {
                if (req.status !== "completed") {
                    await api.patch(`/requests/${req.id}`, { status: "completed" });
                }
                if (!isCurrentlyReplied) await api.post(`/requests/${req.id}/mark-replied`);
            } else if (targetStage === "completed") {
                if (req.status !== "completed") {
                    await api.patch(`/requests/${req.id}`, { status: "completed" });
                }
                if (isCurrentlyReplied) await api.post(`/requests/${req.id}/mark-replied`); // toggle off
            } else {
                // pending / in_progress
                if (req.status !== targetStage) {
                    await api.patch(`/requests/${req.id}`, { status: targetStage });
                }
                if (isCurrentlyReplied) await api.post(`/requests/${req.id}/mark-replied`);
            }
            await onChange();
        } catch { /* noop — toast handled elsewhere if needed */ }
    };

    return (
        <div data-testid="requests-pipeline">
            {/* Top forecast — gross + probability-weighted expected value */}
            {forecast && forecast.total_gross > 0 && (
                <div className="border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 mb-3 flex items-center justify-between gap-4 flex-wrap" data-testid="pipeline-forecast">
                    <div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-emerald-400 mb-1">Pipeline forecast</div>
                        <div className="flex items-baseline gap-3 flex-wrap">
                            <div>
                                <span className="font-heading font-black text-2xl text-zinc-50" data-testid="forecast-gross">{fmtPipelineMoney(forecast.total_gross)}</span>
                                <span className="font-mono text-[10px] text-zinc-500 ml-1">gross</span>
                            </div>
                            <div className="text-zinc-700">·</div>
                            <div>
                                <span className="font-heading font-black text-2xl text-emerald-400" data-testid="forecast-expected">{fmtPipelineMoney(forecast.total_expected)}</span>
                                <span className="font-mono text-[10px] text-emerald-500/70 ml-1">expected</span>
                            </div>
                        </div>
                    </div>
                    <div className="font-mono text-[9px] text-zinc-500 max-w-xs leading-relaxed text-right">
                        Expected = gross × stage-conversion probability.<br />Calibrated to B2B partnership BD funnels.
                    </div>
                </div>
            )}

            {/* Velocity strip — funnel speed at a glance */}
            {velocity && (velocity.avg_days_to_deliver != null || velocity.avg_days_to_reply != null) && (
                <div className="border border-zinc-800 bg-zinc-950/50 px-4 py-2 mb-3 flex items-center gap-5 flex-wrap font-mono text-[10px]" data-testid="velocity-strip">
                    <span className="text-zinc-500 uppercase tracking-[0.25em] text-[9px]">Velocity</span>
                    {velocity.avg_days_to_deliver != null && (
                        <span data-testid="velocity-deliver" className="text-zinc-300">
                            ⚡ deliver in <span className="text-amber-400 font-bold">{velocity.avg_days_to_deliver}d</span>
                            <span className="text-zinc-600 ml-1">avg (n={velocity.sample_sizes.deliver})</span>
                        </span>
                    )}
                    {velocity.avg_days_to_reply != null && (
                        <span data-testid="velocity-reply" className="text-zinc-300">
                            📬 reply in <span className="text-emerald-400 font-bold">{velocity.avg_days_to_reply}d</span>
                            <span className="text-zinc-600 ml-1">avg (n={velocity.sample_sizes.reply})</span>
                        </span>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {PIPELINE_STAGES.map((stage) => {
                    const cards = rows.filter(stage.filter);
                    return (
                        <div
                            key={stage.key}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(stage.key); }}
                            onDragLeave={() => setDragOver((s) => (s === stage.key ? null : s))}
                            onDrop={async (e) => {
                                e.preventDefault();
                                setDragOver(null);
                                if (!dragging) return;
                                const req = rows.find((r) => r.id === dragging);
                                setDragging(null);
                                if (req) await moveTo(req, stage.key);
                            }}
                            className={`border ${stage.tone} bg-zinc-950/50 transition-colors ${dragOver === stage.key ? "bg-amber-500/5 border-amber-500" : ""}`}
                            data-testid={`pipeline-col-${stage.key}`}
                        >
                            <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em]">{stage.label}</div>
                                <span className="font-mono text-[10px] text-zinc-500" data-testid={`pipeline-count-${stage.key}`}>{cards.length}</span>
                            </div>
                            {(() => {
                                const ss = stageStats(stage.key);
                                if (!ss || ss.gross <= 0) return null;
                                return (
                                    <div className="px-3 py-1.5 border-b border-zinc-800/60 bg-black/40 flex items-center justify-between font-mono text-[10px]" data-testid={`pipeline-value-${stage.key}`}>
                                        <span className="text-zinc-300">{fmtPipelineMoney(ss.gross)}</span>
                                        <span className="text-emerald-400">{fmtPipelineMoney(ss.expected)} <span className="text-zinc-600">× {Math.round(ss.probability * 100)}%</span></span>
                                    </div>
                                );
                            })()}
                            {/* Per-column dwell — surfaces how stale cards are in this stage */}
                            {(() => {
                                if (!velocity) return null;
                                const map = {
                                    pending: { v: velocity.avg_pending_dwell, label: "currently sitting" },
                                    completed: { v: velocity.avg_awaiting_dwell, label: "awaiting reply" },
                                };
                                const m = map[stage.key];
                                if (!m || m.v == null) return null;
                                const stale = m.v >= 7;
                                return (
                                    <div className={`px-3 py-1 font-mono text-[9px] flex items-center justify-between border-b border-zinc-800/40 ${stale ? "text-amber-400 bg-amber-500/5" : "text-zinc-500 bg-black/20"}`} data-testid={`pipeline-dwell-${stage.key}`}>
                                        <span>avg dwell</span>
                                        <span className={stale ? "font-bold" : ""}>{m.v}d {stale && "⚠"}</span>
                                    </div>
                                );
                            })()}
                            <div className="p-2 space-y-2 min-h-[120px]">
                                {cards.length === 0 && (
                                    <div className="text-center font-mono text-[9px] text-zinc-700 italic py-6">drop here</div>
                                )}
                                {cards.map((r) => (
                                    <PipelineCard
                                        key={r.id}
                                        r={r}
                                        onDragStart={() => setDragging(r.id)}
                                        onDragEnd={() => setDragging(null)}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
            {declined.length > 0 && (
                <details className="mt-4 border border-zinc-800" data-testid="pipeline-declined">
                    <summary className="px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 cursor-pointer hover:text-zinc-300">
                        Declined ({declined.length})
                    </summary>
                    <div className="p-2 space-y-2">
                        {declined.map((r) => <PipelineCard key={r.id} r={r} onDragStart={() => {}} onDragEnd={() => {}} draggable={false} />)}
                    </div>
                </details>
            )}
            <div className="font-mono text-[9px] text-zinc-600 mt-3" data-testid="pipeline-help">
                Drag cards between columns to update status. Dropping into "Replied" auto-marks the reply timestamp.
            </div>
        </div>
    );
}

function PipelineCard({ r, onDragStart, onDragEnd, draggable = true }) {
    const cfg = REQUEST_TYPES.find((t) => t.key === r.type);
    const Icon = cfg?.icon || Sparkles;
    const tk = detectShareToken(r.result_link);
    const headline = r.payload?.firm_name || r.payload?.firm_a_name || r.payload?.firm_b_name || cfg?.label || r.type;
    const subline = r.payload?.firm_b_name && r.payload?.firm_a_name
        ? `${r.payload.firm_a_name} × ${r.payload.firm_b_name}`
        : (r.client_name || r.client_email || "");

    return (
        <div
            draggable={draggable}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            data-testid={`pipeline-card-${r.id}`}
            className={`border border-zinc-800 bg-black p-2.5 ${draggable ? "cursor-grab active:cursor-grabbing hover:border-zinc-700" : ""}`}
        >
            <div className="flex items-center gap-1.5 mb-1">
                <Icon className="w-3 h-3 text-amber-400 shrink-0" />
                <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${cfg?.tone || "border-zinc-700 text-zinc-400"}`}>{cfg?.label || r.type}</span>
                {r.batch_id && <span className="font-mono text-[8px] uppercase tracking-wider text-amber-500/70" title="Part of a batch">batch</span>}
            </div>
            <div className="text-sm text-zinc-100 font-semibold leading-tight truncate" title={headline}>{headline}</div>
            {subline && <div className="font-mono text-[10px] text-zinc-500 truncate mt-0.5" title={subline}>{subline}</div>}
            <div className="flex items-center justify-between gap-2 mt-1.5">
                <div className="font-mono text-[9px] text-zinc-600">{new Date(r.created_at).toLocaleDateString()}</div>
                {tk && (
                    <a
                        href={`/p/${tk.kind === "firm" ? "firm/" : ""}${tk.token}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="font-mono text-[9px] text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-0.5"
                    >
                        <ExternalLink className="w-2.5 h-2.5" /> link
                    </a>
                )}
            </div>
        </div>
    );
}


// Reply-rate metric — % of completed-with-public-link requests that have been marked replied.
// Polls on mount + whenever the rows count changes (mark-replied triggers a reload upstream).
function ReplyRateBadge({ refreshKey }) {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        let cancelled = false;
        api.get("/requests/reply-stats")
            .then(({ data }) => { if (!cancelled) setStats(data); })
            .catch(() => { if (!cancelled) setStats(null); });
        return () => { cancelled = true; };
    }, [refreshKey]);
    if (!stats || stats.total_completed === 0) return null;
    const tone = stats.reply_rate_pct >= 25 ? "emerald" : stats.reply_rate_pct >= 10 ? "amber" : "zinc";
    const cls = {
        emerald: "border-emerald-500/50 bg-emerald-500/5 text-emerald-300",
        amber: "border-amber-500/50 bg-amber-500/5 text-amber-300",
        zinc: "border-zinc-800 bg-zinc-950 text-zinc-300",
    }[tone];
    return (
        <div className={`border ${cls} px-4 py-2.5 flex items-center gap-3`} data-testid="reply-rate-badge" title="Reply rate = replied / completed deliverables. Mark a request as replied when a prospect emails you back.">
            <Reply className="w-4 h-4 shrink-0" />
            <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] opacity-70">Reply rate</div>
                <div className="font-heading font-black text-xl leading-none" data-testid="reply-rate-pct">{stats.reply_rate_pct}<span className="text-xs opacity-60">%</span></div>
            </div>
            <div className="font-mono text-[9px] text-zinc-500 leading-tight pl-2 border-l border-zinc-800">
                {stats.total_replied} of<br />{stats.total_completed} sent
            </div>
        </div>
    );
}

